import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar,
  Divider, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  AccountBalance, Category, Book, ManageAccounts, Settings,
  Menu as MenuIcon, Church, Logout, Assessment,
} from '@mui/icons-material';
import { useNotify } from '../notify';

const DRAWER_WIDTH = 245;

const NAV_ITEMS = [
  { label: 'Cash Book',      icon: <Book />,           path: '/',              permission: 'cash_book' },

  { section: 'Setup' },
  { label: 'Income Types',   icon: <Category />,       path: '/income-types',  permission: 'income_types' },
  { label: 'Expense Types',  icon: <Category />,       path: '/expense-types', permission: 'expense_types' },
  { label: 'Bank Accounts',  icon: <AccountBalance />, path: '/bank-accounts', permission: 'bank_accounts' },

  { section: 'Reports' },
  { label: 'Reports',        icon: <Assessment />,     path: '/reports',       permission: 'reports' },

  { section: 'Admin' },
  { label: 'Users',          icon: <ManageAccounts />, path: '/users',         permission: 'users',    roles: ['superadmin','admin'] },
  { label: 'Settings',       icon: <Settings />,       path: '/settings',      permission: 'settings', roles: ['superadmin','admin'] },
];

const ROLE_COLORS = {
  superadmin: '#C89211',
  admin:      '#E0A82E',
  accountant: '#10B981',
  viewer:     '#94A3B8',
};

export default function Layout({ children, user, churchInfo, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme    = useTheme();
  const notify   = useNotify();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const role  = user?.role;
  const perms = user?.permissions || {};

  const canSee = ({ roles, permission }) => {
    if (roles && !roles.includes(role)) return false;
    if (role === 'superadmin') return true;
    if (permission && !perms[permission]) return false;
    return true;
  };

  const handleLogout = async () => {
    const ok = await notify.confirm({
      title: 'Sign out?',
      message: 'You will need to enter your credentials again to return.',
      confirmLabel: 'Sign out',
      cancelLabel: 'Stay signed in',
      severity: 'info',
    });
    if (ok) onLogout();
  };

  const churchName = churchInfo?.name || 'The Church';
  const churchLogo = churchInfo?.logo;

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, #0B2545 0%, #061529 100%)' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {churchLogo ? (
          <Avatar src={churchLogo} sx={{ width: 40, height: 40 }} />
        ) : (
          <Box sx={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Church sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
        )}
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }} noWrap>
            {churchName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#E0A82E', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Accounting
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />

      <List sx={{ px: 1, pt: 1, flex: 1, overflowY: 'auto' }}>
        {NAV_ITEMS.map((item, i) => {
          if (item.section) {
            return (
              <Typography key={`section-${i}`} variant="caption"
                sx={{
                  display: 'block', px: 2, mt: 2, mb: 0.5,
                  color: 'rgba(255,255,255,0.45)', fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.14em', textTransform: 'uppercase',
                }}>
                {item.section}
              </Typography>
            );
          }
          if (!canSee(item)) return null;
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.4 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                sx={{
                  borderRadius: 2,
                  backgroundColor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                  py: 0.9,
                }}
              >
                <ListItemIcon sx={{ color: active ? '#fff' : 'rgba(255,255,255,0.6)', minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 600 : 400, color: active ? '#fff' : 'rgba(255,255,255,0.75)' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 14, flexShrink: 0 }}>
          {user?.full_name?.[0] || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, display: 'block', lineHeight: 1.2 }} noWrap>
            {user?.full_name || 'User'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ROLE_COLORS[role] || '#aaa', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, textTransform: 'capitalize' }}>
              {role}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Sign out">
          <IconButton onClick={handleLogout} size="small" sx={{ color: 'rgba(255,255,255,0.65)' }}>
            <Logout fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            background: 'linear-gradient(90deg, #0B2545, #13315C)',
            boxShadow: '0 2px 12px rgba(11,37,69,0.15)',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2 } }}>
            <IconButton color="inherit" onClick={() => setMobileOpen(true)} edge="start" sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
            {churchLogo ? (
              <Avatar src={churchLogo} sx={{ width: 32, height: 32, mr: 1 }} />
            ) : (
              <Box sx={{
                width: 32, height: 32, borderRadius: 1,
                bgcolor: '#C89211',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1,
              }}>
                <Church sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1" noWrap
                sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.1 }}
              >
                {churchName}
              </Typography>
              <Typography
                variant="caption" noWrap
                sx={{ color: '#E0A82E', fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}
              >
                Accounting
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {isMobile ? (
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 'none' } }}>
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent"
          sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 'none' } }}>
          {drawer}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,           // allow flex children to shrink below their content
          p: { xs: 1.5, sm: 2, md: 3 },
          pt:  isMobile ? { xs: 9, sm: 9 } : { md: 3 },
          // Fixed height (not minHeight) so pages can bound themselves to the
          // viewport and scroll their own panes instead of the whole document.
          height: '100dvh',
          background: '#F5F7FA',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
